import type { RichDoc, RichMark, RichNode } from '@praesto/shared'

/**
 * Ausgezeichneten Text anzeigen — ohne Editor.
 *
 * Der Editor läuft nur, während jemand tippt. Angezeigt wird der Text an
 * hunderten Stellen ohne ihn: auf dem Board, in der Gast-Vorschau, im Verlauf,
 * in Vorlagen. Deshalb steht hier ein eigener, kleiner Wiedergeber über der
 * abgelegten Struktur statt einer zweiten Editor-Instanz je Element.
 *
 * Grundsatz bei Unbekanntem: Text geht nie verloren. Ein Knoten, den dieser
 * Wiedergeber nicht kennt, wird über seine Kinder ausgegeben; eine unbekannte
 * Auszeichnung wird übergangen. Schlimmstenfalls fehlt eine Hervorhebung.
 */

/** Was ein Absatz an Ausrichtung tragen kann. */
type Ausrichtung = 'left' | 'center' | 'right' | 'justify'

const AUSRICHTUNGEN: readonly string[] = ['left', 'center', 'right', 'justify']

function ausrichtungVon(attrs: Record<string, unknown> | undefined): Ausrichtung | undefined {
  const wert = attrs?.textAlign
  return typeof wert === 'string' && AUSRICHTUNGEN.includes(wert) ? (wert as Ausrichtung) : undefined
}

/** Farbe aus der `textStyle`-Auszeichnung; alles andere wird nicht übernommen. */
function farbeVon(marks: readonly RichMark[] | undefined): string | undefined {
  for (const mark of marks ?? []) {
    if (mark.type !== 'textStyle') continue
    const farbe = mark.attrs?.color
    if (typeof farbe === 'string' && farbe !== '') return farbe
  }
  return undefined
}

function stilVon(marks: readonly RichMark[] | undefined): React.CSSProperties | undefined {
  const namen = new Set((marks ?? []).map((mark) => mark.type))
  const farbe = farbeVon(marks)
  if (namen.size === 0 && !farbe) return undefined

  const stil: React.CSSProperties = {}
  if (farbe) stil.color = farbe
  if (namen.has('bold')) stil.fontWeight = 700
  if (namen.has('italic')) stil.fontStyle = 'italic'
  // Unterstrichen und durchgestrichen können zusammen auftreten.
  const linien = [namen.has('underline') && 'underline', namen.has('strike') && 'line-through']
    .filter(Boolean)
    .join(' ')
  if (linien) stil.textDecoration = linien
  return Object.keys(stil).length > 0 ? stil : undefined
}

function kinder(nodes: readonly RichNode[] | undefined, praefix: string): React.ReactNode {
  return nodes?.map((node, index) => knoten(node, `${praefix}.${index}`))
}

function knoten(node: RichNode, schluessel: string): React.ReactNode {
  if (node.type === 'text') {
    const stil = stilVon(node.marks)
    const text = node.text ?? ''
    return stil ? (
      <span key={schluessel} style={stil}>
        {text}
      </span>
    ) : (
      text
    )
  }

  const ausrichtung = ausrichtungVon(node.attrs)
  const stil = ausrichtung ? { textAlign: ausrichtung } : undefined

  switch (node.type) {
    case 'paragraph':
      /* Ein leerer Absatz ist eine Leerzeile und muss Höhe behalten — ohne den
         Zeilenumbruch fiele er auf null zusammen. */
      return (
        <p key={schluessel} style={stil}>
          {node.content && node.content.length > 0 ? kinder(node.content, schluessel) : <br />}
        </p>
      )
    case 'heading': {
      const stufe = Number(node.attrs?.level ?? 1)
      const Tag = (`h${Math.min(3, Math.max(1, stufe))}` as unknown) as 'h1'
      return (
        <Tag key={schluessel} style={stil}>
          {kinder(node.content, schluessel)}
        </Tag>
      )
    }
    case 'bulletList':
      return <ul key={schluessel}>{kinder(node.content, schluessel)}</ul>
    case 'orderedList':
      return <ol key={schluessel}>{kinder(node.content, schluessel)}</ol>
    case 'listItem':
      return <li key={schluessel}>{kinder(node.content, schluessel)}</li>
    case 'blockquote':
      return <blockquote key={schluessel}>{kinder(node.content, schluessel)}</blockquote>
    case 'hardBreak':
      return <br key={schluessel} />
    case 'horizontalRule':
      return <hr key={schluessel} />
    default:
      return <span key={schluessel}>{kinder(node.content, schluessel)}</span>
  }
}

export function RichText({ doc }: { doc: RichDoc }): React.ReactElement {
  return <>{kinder(doc.content, 'r')}</>
}

/** Knoten, die in der reinen Textfassung eine Zeile abschließen. */
const ZEILENENDE = new Set(['paragraph', 'heading', 'listItem', 'blockquote'])

function sammleText(node: RichNode): string {
  if (node.type === 'text') return node.text ?? ''
  if (node.type === 'hardBreak') return '\n'
  const inhalt = (node.content ?? []).map(sammleText).join('')
  return ZEILENENDE.has(node.type) ? `${inhalt}\n` : inhalt
}

/** Reine Textfassung — für Ebenenliste, Vorschau und Suche. */
export function plainTextOf(doc: RichDoc): string {
  return (doc.content ?? [])
    .map(sammleText)
    .join('')
    .replace(/\n+$/, '')
}

/**
 * Alter Text ohne Auszeichnung wird zu einer Struktur.
 *
 * Absätze entstehen an Zeilenumbrüchen — so, wie der frühere mehrzeilige
 * Textbereich sie gemeint hat.
 */
export function richFromPlain(text: string): RichDoc {
  const zeilen = text.split('\n')
  return {
    type: 'doc',
    content: zeilen.map((zeile) => ({
      type: 'paragraph',
      ...(zeile === '' ? {} : { content: [{ type: 'text', text: zeile }] }),
    })),
  }
}

/** Prüft grob, ob eine abgelegte Struktur brauchbar ist. */
export function isRichDoc(value: unknown): value is RichDoc {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'doc' &&
    (!('content' in value) || Array.isArray((value as { content?: unknown }).content))
  )
}
